import React, { useEffect, useState } from "react"
import types from "prop-types"
import { cloneDeep, isEmpty } from "lodash-es"
import * as API from "services/api"
import { useCustomName } from "hooks"
import { DEPARTMENT_KEY } from "utils/customNameHelpers"
import { SEGMENTS } from "utils/departmentSegmentHelpers"
import { STANDARD_ACCESS, NONE_ACCESS } from "utils/accessLevelHelpers"
import { workflowActionShape } from "utils/propTypeShapes"
import { actionValidations } from "utils/workflowHelpers"
import { LOCATION_TYPE } from "views/Forms/FormElementTypes"
import { Checkbox } from "shared/checkboxes"
import { AccessLevelSelect, Select } from "shared/selects"
import FormsyValidation from "shared/FormsyValidation"
import ItemRow from "shared/ItemRow"

const EMPTY_DATA = {
  questionUUID: null,
  segments: {},
}

const DefineAddDepartmentSegmentAccessData = ({
  action, actionIndex, changeActionData, formSlug,
}) => {
  const { data = {} } = action
  const {
    questionUUID: selectedQuestionUuid = null,
    segments: initialSegmentAccessLevels = {},
  } = data

  const [locationQuestions, setLocationQuestions] = useState([])
  const [segmentAccessLevels, setSegmentAccessLevels] = useState(initialSegmentAccessLevels)

  const {
    name: departmentName,
    nameInSentence: departmentNameInSentence,
  } = useCustomName(DEPARTMENT_KEY)

  useEffect(() => {
    if (!data.segments || Object.keys(data.segments).length === 0) {
      changeActionData(actionIndex)(cloneDeep(EMPTY_DATA))
    }
    // data.segments intentionally omitted — adding it would cause a loop since
    // the initialization itself updates data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionIndex, changeActionData])

  useEffect(() => {
    changeActionData(actionIndex)({
      ...data,
      segments: segmentAccessLevels,
    })
    // data intentionally omitted — calling changeActionData updates data via the
    // parent, which would re-trigger this effect causing an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionIndex, changeActionData, segmentAccessLevels])

  // Load the form's location questions when the form slug changes.
  // Loading the questions ensures that the workflow references persisted
  // questions and not question that have not been saved.
  useEffect(() => {
    (async function fetchFormQuestions() {
      if (formSlug) {
        const response = await API.getFormQuestions({ formSlug, type: LOCATION_TYPE })

        if (response.ok) {
          setLocationQuestions(response.data)
        } else {
          console.error("Error fetching location questions: ", response)
        }
      }
    }())
  }, [formSlug])

  const updateQuestionUuid = (newQuestionUuid) => {
    const updatedData = {
      ...data,
      questionUUID: newQuestionUuid,
    }

    changeActionData(actionIndex)(updatedData)
  }

  // When the location questions change, ensure that one of them is selected
  useEffect(() => {
    if (!isEmpty(locationQuestions)) {
      updateQuestionUuid(locationQuestions[0].uuid)
      return
    }

    if (selectedQuestionUuid) {
      updateQuestionUuid(null)
    }
    // updateQuestionUuid/selectedQuestionUuid intentionally omitted — adding them
    // would risk re-triggering after changeActionData updates data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationQuestions])

  const selectSegment = (segment, accessLevel) => {
    const updatedSegments = {
      ...segmentAccessLevels,
      [segment]: accessLevel,
    }

    setSegmentAccessLevels(updatedSegments)
  }

  const deselectSegment = (segmentToRemove) => {
    const updatedSegments = { ...segmentAccessLevels }
    delete updatedSegments[segmentToRemove]

    setSegmentAccessLevels(updatedSegments)
  }

  const handleAccessLevelChange = (value, segment) => {
    const updatedSegments = {
      ...segmentAccessLevels,
      [segment]: value,
    }

    setSegmentAccessLevels(updatedSegments)
  }

  const questionOptions = locationQuestions.map((question) => ({
    value: question.uuid,
    label: question.prompt,
  }))

  const validations = actionValidations(action.type)

  if (isEmpty(locationQuestions)) return null

  return (
    <div className="w-full mt-4">
      <div className="mb-4">
        <label>
          <span className="font-semibold required-prompt inline-block mb-1">
            Location question
          </span>
          <Select
            backspaceDelete={false}
            options={questionOptions}
            value={selectedQuestionUuid}
            style={{ height: "32px" }}
            placeholder="Select question from the form"
            onChange={updateQuestionUuid}
            label={`select location question to determine notified ${departmentNameInSentence}`}
          />
        </label>
      </div>
      <span className="font-semibold required-prompt inline-block mb-1">
        {departmentName} Segments
      </span>
      <div className="flex flex-col gap-1">
        {
          SEGMENTS.map((segment) => {
            const checkBoxId = `workflow-action-${actionIndex}-add-department-segment-${segment}-checkbox`
            const selectId = `workflow-action-${actionIndex}-add-department-segment-${segment}-access-select`
            const label = `Segment ${segment.toUpperCase()}`
            const selected = segment in segmentAccessLevels
            const toggleSelection = () => (selected ? deselectSegment(segment) : selectSegment(segment, STANDARD_ACCESS))
            const accessLevel = segmentAccessLevels[segment] || STANDARD_ACCESS

            return (
              <ItemRow
                key={segment}
                itemRowHeaderContent={(
                  <>
                    <div className="py-3">
                      <Checkbox
                        uuid={checkBoxId}
                        className="cursor-pointer mr-2"
                        value={selected}
                        onChange={toggleSelection}
                      />
                      <label htmlFor={checkBoxId} className="cursor-pointer">
                        {label}
                      </label>
                    </div>
                    {selected && (
                      <div className="justify-start">
                        <AccessLevelSelect
                          key={selectId}
                          onChange={(value) => handleAccessLevelChange(value, segment)}
                          value={accessLevel}
                          optionsToOmit={[NONE_ACCESS]}
                        />
                      </div>
                    )}
                  </>
                )}
              />
            )
          })
        }
      </div>
      <FormsyValidation
        name={`actions[${actionIndex}]`}
        value={action}
        validations={{ validations }}
      />
    </div>
  )
}

DefineAddDepartmentSegmentAccessData.propTypes = {
  action: workflowActionShape.isRequired,
  changeActionData: types.func.isRequired,
  formSlug: types.string.isRequired,
  actionIndex: types.number.isRequired,
}

export default DefineAddDepartmentSegmentAccessData
